import type { AdminCampaignSummary } from "../../api/types";
import { LIFECYCLE_STATE_ORDER } from "../../formatting/status";

/**
 * feat/campaign-list-sort-show-column: pure sort helpers for the 5 sortable
 * "Campañas" list columns (Campaña/Estado/Programada para/Show/Plantilla —
 * user follow-up 2026-09-25: "faltó el de Estado, Programada para, Show y
 * Plantilla", this file originally only covered Campaña). Kept in its OWN
 * module (same convention as `filters.ts` next to it) rather than inside
 * `CampaignListScreen.tsx` — a function exported alongside a component from
 * the same file trips `react-refresh/only-export-components`, and this is
 * also what makes it unit-testable in isolation from the screen's
 * rendering/query wiring.
 *
 * DISCLOSED LIMITATION (product decision, see
 * odd/tasks/campaign-list-sort-show-column.md): this sorts only the rows
 * already loaded for the CURRENT page — there is no backend sort parameter,
 * so changing page resets to the backend's fixed order
 * (`fecha_programada DESC`). Accepted tradeoff: fast, frontend-only, no
 * backend change required.
 */
export type SortColumn = "nombre" | "estado" | "fechaProgramada" | "showName" | "templateName";

export interface SortState {
  column: SortColumn | null;
  direction: "asc" | "desc" | null;
}

export const INITIAL_SORT: SortState = { column: null, direction: null };

// "Estado" sorts by the campaign's LOGICAL lifecycle position (borrador →
// programada → enviando → parcial → fallida → completada → cancelada), never
// plain alphabetical — alphabetically these interleave meaninglessly
// ("cancelada" before "completada" before "enviando"...), while the lifecycle
// order groups campaigns the way an operator actually thinks about them.
const LIFECYCLE_RANK: Readonly<Record<string, number>> = Object.fromEntries(
  LIFECYCLE_STATE_ORDER.map((estado, index) => [estado, index]),
);

function compareByColumn(a: AdminCampaignSummary, b: AdminCampaignSummary, column: SortColumn): number {
  switch (column) {
    case "nombre":
      return a.nombre.localeCompare(b.nombre, "es");
    case "templateName":
      return a.templateName.localeCompare(b.templateName, "es");
    case "showName":
      // Null-handling lives in `sortCampaigns` (missing Show always sorts
      // last, regardless of direction) — this branch only runs for two
      // already-non-null values.
      return (a.showName ?? "").localeCompare(b.showName ?? "", "es");
    case "estado": {
      const rankA = LIFECYCLE_RANK[a.estado] ?? Number.MAX_SAFE_INTEGER;
      const rankB = LIFECYCLE_RANK[b.estado] ?? Number.MAX_SAFE_INTEGER;
      return rankA - rankB;
    }
    case "fechaProgramada":
      return new Date(a.fechaProgramada).getTime() - new Date(b.fechaProgramada).getTime();
  }
}

export function sortCampaigns(
  campaigns: readonly AdminCampaignSummary[],
  sort: SortState,
): AdminCampaignSummary[] {
  if (sort.column === null || sort.direction === null) {
    return [...campaigns];
  }
  const column = sort.column;
  const factor = sort.direction === "asc" ? 1 : -1;

  if (column === "showName") {
    // issue #330: `showName` is `null` for most rows until the backend
    // ships the join — a campaign with no Show data should never visually
    // jump to the top just because the direction flipped; it stays at the
    // bottom in BOTH ascending and descending order.
    return [...campaigns].sort((a, b) => {
      const aMissing = a.showName == null;
      const bMissing = b.showName == null;
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      return factor * compareByColumn(a, b, column);
    });
  }

  return [...campaigns].sort((a, b) => factor * compareByColumn(a, b, column));
}

/**
 * Standard single-active-column table-sort UX: clicking a DIFFERENT column
 * starts it fresh at ascending (never inherits the previous column's
 * direction); clicking the SAME column cycles asc → desc → back to the
 * original (unsorted) order.
 */
export function nextSort(current: SortState, clickedColumn: SortColumn): SortState {
  if (current.column !== clickedColumn) {
    return { column: clickedColumn, direction: "asc" };
  }
  if (current.direction === "asc") {
    return { column: clickedColumn, direction: "desc" };
  }
  return INITIAL_SORT;
}
